<template>
    <Portal to="drawer">
        <div class="drawer-placeholder" :class="{ active: menuState !== 'closed' }">
            <div ref="drawer" class="drawer-wrapper" :style="`--drawer-scroll-progress: ${progressY}`">
                <div ref="drawerHandle" class="handle">
                    <div class="line"></div>
                </div>
                <div class="drawer">
                    <div ref="overflowDrawer" class="options-wrapper" :class="{ 'disable-scroll': !scrollEnabled }">
                        <div v-for="option in options" :key="option.value" class="option" :class="{ selected: value.includes(option.value) }">
                            <label :for="`option-${option.value}`" class="option-label">
                                <div class="left">
                                    <input 
                                        :id="`option-${option.value}`"
                                        type="checkbox"
                                        class="hidden-checkbox" 
                                        @change="handleCheckboxUpdated(option)" 
                                    />
                                    <svg class="checkbox-icon" viewBox="0 0 512 512">
                                        <rect class="rect" x="64" y="64" width="384" height="384" rx="48" ry="48" fill="none" stroke="currentColor" stroke-linejoin="round" stroke-width="32"/>
                                        <path class="check" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="32" d="M352 176L217.6 336 160 272"/>
                                    </svg>
                                    <span>{{ option.label }}</span>
                                </div>
                                <div class="right">
                                    <interactive v-if="favorites" class="flex align-center" @click.stop.prevent="$emit('toggleFavorite', option.value)" jumpSize="big">
                                        <svg v-if="!option.favorite" class="star-icon empty" width="24" height="24" fill="#515151" viewBox="0 0 16 16">
                                            <path d="M2.866 14.85c-.078.444.36.791.746.593l4.39-2.256 4.389 2.256c.386.198.824-.149.746-.592l-.83-4.73 3.522-3.356c.33-.314.16-.888-.282-.95l-4.898-.696L8.465.792a.513.513 0 0 0-.927 0L5.354 5.12l-4.898.696c-.441.062-.612.636-.283.95l3.523 3.356-.83 4.73zm4.905-2.767-3.686 1.894.694-3.957a.56.56 0 0 0-.163-.505L1.71 6.745l4.052-.576a.53.53 0 0 0 .393-.288L8 2.223l1.847 3.658a.53.53 0 0 0 .393.288l4.052.575-2.906 2.77a.56.56 0 0 0-.163.506l.694 3.957-3.686-1.894a.5.5 0 0 0-.461 0z"/>
                                        </svg>
                                        <svg v-else class="star-icon filled" width="24" height="24" fill="#e8b923" viewBox="0 0 16 16">
                                            <path d="M3.612 15.443c-.386.198-.824-.149-.746-.592l.83-4.73L.173 6.765c-.329-.314-.158-.888.283-.95l4.898-.696L7.538.792c.197-.39.73-.39.927 0l2.184 4.327 4.898.696c.441.062.612.636.282.95l-3.522 3.356.83 4.73c.078.443-.36.79-.746.592L8 13.187l-4.389 2.256z"/>
                                        </svg>
                                    </interactive>
                                    <avatar class="user-avatar" :class="{ hasAvatar: option.base64avatar }" :userId="option.value" :avatar="option.base64avatar"></avatar>
                                </div>
                            </label>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </Portal>
</template>

<script>
import Teleport from 'vue';
import Avatar from '@/components/Avatar.vue';
import Interactive from '@/components/Interactive.vue';

export default {
    name: 'custom-drawer',
    components: {
        Teleport,
        Avatar,
        Interactive,
    },
    props: {
        value: {
            type: Array,
            required: true
        },
        options: {
            type: Array,
            default() { return [] }
        },
        favorites: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            progressY: 0,
            scrolling: false,
            startingPositionY: 0,
            previousY: 0,
            speed: 0,
            drawerSpeed: 0,
            finishScrollData: {
                from: 0,
                to: 0
            },
            menuState: 'closed',
            disableMenuScrolling: false,
            scrollEnabled: true,
            scrolledToTop: true,
            scrollSpeed: 0,
            previousScrollTop: 0,
            animating: false,
        }
    },
    methods: {
        handleTargetElement(event) {
            if(this.hideMenu) return
            const target = event.target;

            if(this.$refs.drawer.contains(target)) {
                this.scrolling = true;
                this.speed = 0;
                this.previousY = 0;
                this.maxPositionY = Math.min(this.$refs.drawer.scrollHeight / window.innerHeight, 1);
            }
        },
        handleDrawerScroll() {
            const drawer = this.$refs.overflowDrawer;
            const scrollTop = drawer.scrollTop;
            if(scrollTop === 0 && !this.scrollEnabled) {
                this.scrollEnabled = true;
            }
            
            this.drawerSpeed = scrollTop - this.previousScrollTop;
            this.scrolledToTop = Math.floor(scrollTop) === 0;
            this.previousScrollTop = scrollTop;
        },
        handlePageScroll(event) {
            this.scrollEnabled = true;
            if(this.disableMenuScrolling) return

            const drawer = this.$refs.overflowDrawer;
            const scrollTop = drawer.scrollTop;
            this.scrolledToTop = Math.floor(scrollTop) === 0;
            
            const touchEvent = event.targetTouches[0];
            const positionY = touchEvent.clientY;
            if(!this.scrolling && this.menuState === 'closed') return this.disableMenuScrolling = true
            
            if(!this.startingPositionY && this.scrolledToTop) this.startingPositionY = positionY;
            
            if(this.previousY) this.startingPositionY = this.previousY;

            const relativeY = (this.startingPositionY - positionY) / window.innerHeight;
            const handle = this.$refs.drawerHandle;

            if(!handle.contains(event.target) && this.menuState !== 'closed' && !(relativeY < 0 && this.scrolledToTop)) return this.handleDrawerScroll(event)
            else this.scrollEnabled = false;

            const isScrolling = this.$refs.overflowDrawer.scrollTop !== 0;
            if(!handle.contains(event.target) && isScrolling) return

            let speed = positionY - this.previousY;
            if(this.previousY) this.speed = speed;

            let deltaY = 0;
            // if(this.menuState === 'open') deltaY = (this.startingPositionY / window.innerHeight) * this.progressY;

            this.scrolling = true;
            
            const step = this.progressY + relativeY + deltaY;
            let scrollPosition = step;
            if(scrollPosition > this.maxPositionY) scrollPosition = this.maxPositionY;
            if(scrollPosition < 0) scrollPosition = 0;
            if(scrollPosition > 1) scrollPosition = 1;
            this.progressY = scrollPosition;
            this.previousY = positionY;
        },
        updateScrollPosition(timestamp) {
            if (!this.animating) return

            if(this.scrolling) return this.start = null;
            
            const duration = 600;
            if(!this.start) this.start = timestamp;
            var progress = timestamp - this.start;
            let x = progress/duration;
            if(x > 1) x = 1;
            if(x < 0) x = 0;
            const newX = 1 - Math.pow(1 - x, 5);

            const deltaY = this.finishScrollData.to - this.finishScrollData.from;
            
            let updatedprogressY = 0;
            
            let speed = Math.max(Math.min(this.speed/30, 0.4), -0.4);
            const showSpeed = this.menuState === 'open' ? speed < 0 : speed > 0;
            if(speed < 0) speed = speed * -1;
            const speedX = progress / (duration/2);
            const newSpeedX = 1 - Math.pow(1 - speedX, 5);
            let appliedSpeed = 0;
            if(newSpeedX <= 1 && !this.menuToggled && showSpeed) appliedSpeed = speed * Math.min(newSpeedX, 1-newSpeedX);

            if(deltaY > 0) updatedprogressY = this.finishScrollData.from + (newX*deltaY) - appliedSpeed;
            else updatedprogressY = this.finishScrollData.from + (newX*deltaY) + appliedSpeed;

            this.progressY = updatedprogressY;

            if(x < 1) window.requestAnimationFrame(this.updateScrollPosition);
            else {
                this.animating = false;
                this.start = null;
                this.speed = 0;
                this.drawerSpeed = 0;
                this.menuToggled = false;
                this.startingPositionY = 0;
                this.scrolledToTop = true;
                this.finishScrollData = {
                    from: 0,
                    to: 0
                }
                if(this.progressY === 1) this.handleDrawerScroll()
            }
        },
        async handleFinishScroll(progressY) {
            this.animating = true;

            if(progressY > this.maxPositionY) progressY = this.maxPositionY;

            let newMenuState = '';
            if(progressY === 1) newMenuState = 'open';
            else if(progressY === .6) newMenuState = 'halfway';
            else newMenuState = 'closed';

            this.finishScrollData = {
                from: this.progressY,
                to: progressY
            }
            this.menuToggled = this.menuState !== newMenuState;
            this.direction = this.progressY > progressY ? 'down' : 'up';
            this.menuState = newMenuState;
            window.requestAnimationFrame(this.updateScrollPosition);
        },
        handleUnfocus(target) {
            this.disableMenuScrolling = false;
            if(this.progressY > 0 && !this.$refs.drawer.contains(target)) return this.handleFinishScroll(0)
            if(!this.scrolling) return

            this.scrolling = false;
            this.startingPositionY = 0;
            this.previousY = 0;
            const speed = Math.max(Math.min(this.speed/30, 0.4), -0.4);
            const drawerSpeed = Math.max(Math.min(this.drawerSpeed/200, 0.6), -0.6);

            if(this.progressY === 1 && this.scrolledToTop) return

            if(this.menuState !== 'closed' && drawerSpeed === -0.6) this.handleFinishScroll(0);
            else if(this.menuState !== 'closed' && drawerSpeed === 0.6) this.handleFinishScroll(1);
            else if(this.progressY >= this.maxPositionY) this.handleFinishScroll(this.maxPositionY);
            else if(this.progressY > .75 + speed) this.handleFinishScroll(1);
            else if(this.progressY > .25 + speed) this.handleFinishScroll(.6);
            else this.handleFinishScroll(0);
        },
        openDrawer() {
            const drawer = this.$refs.drawer;
            const drawerHeight = drawer.scrollHeight;
            const canFillScreen = drawerHeight >= window.innerHeight;

            this.finishScrollData = {
                from: this.progressY,
                to: .6
            }

            // this.progressY = 0;
            // this.scrolling = false;
            // this.startingPositionY = 0;
            // this.previousY = 0;

            // this.speed = 0;
            // this.drawerSpeed = 0;
            // // finishScrollData: {
            // //     from: 0,
            // //     to: 0
            // // },
            // this.menuState = 'halfway';
            // this.disableMenuScrolling = true;
            // this.scrollEnabled = false;
            // this.scrolledToTop = false;
            // // scrollSpeed: 0,
            // this.previousScrollTop = 1;

            this.handleFinishScroll(.6);
        },
        handleCheckboxUpdated(option) {
            let value = this.value;

            if (!value.includes(option.value)) value.push(option.value);
            else value = value.filter(value => value !== option.value);

            this.$emit('input', value);
            this.$emit('optionUpdated');
        }
    },
    async mounted() {
        const vm = this;
        this.$root.$on('targetElement', vm.handleTargetElement);
        this.$root.$on('targetElementMoved', vm.handlePageScroll);
        this.$root.$on('clickElement', vm.handleUnfocus);
    },
    beforeDestroy() {
        const vm = this;
        this.$root.$off('targetElement', vm.handleTargetElement);
        this.$root.$off('targetElementMoved', vm.handlePageScroll);
        this.$root.$off('clickElement', vm.handleUnfocus);
    }
}
</script>

<style lang="scss" scoped>
@import '@/assets/variables.scss';

.drawer-placeholder {
    position: fixed;
    top: 50%;
    left: 50%;
    translate: -50% -50%;
    height: 100dvh;
    width: 100dvw;
    pointer-events: none;
    z-index: 2000;

    &.active {
        pointer-events: all;
    }
}

.drawer-wrapper {
    position: fixed;
    top: 0;
    margin-top: calc(100dvh - (var(--drawer-scroll-progress) * 100dvh));
    inset-inline: 0;

    z-index: 2001;
    pointer-events: all;

    .handle {
        display: grid;
        place-items: center;
        background: #182118ee;
        border-radius: 8vw 8vw 0 0;
        height: 40px;

        .line {
            width: 25%;
            height: 2px;
            border-radius: 1px;
            background: grey;
        }
    }

    .drawer {
        background: #182118ee;
        max-height: calc(100dvh - 40px);
        min-height: calc(100dvh - 40px);
        overflow: none;
        overscroll-behavior: none;
        
        .options-wrapper {
            display: flex;
            flex-direction: column;
            gap: 6px;
            padding: 8px 12px;
            max-height: calc((var(--drawer-scroll-progress) * 100dvh) - 40px - 16px);
            overflow-y: auto;

            &.disable-scroll {
                overflow-y: hidden;
            }

            .option {
                --avatar-size: 48px;
                position: relative;
                border-radius: 12px calc(var(--avatar-size) / 2 + 1px) calc(var(--avatar-size) / 2 + 1px) 12px;
                transition: .2s ease;
                border: 1px solid #515551;;

                &.selected {
                    background: #0c2a0c63;
                    border-color: $weed-green;

                    .option-label {
                        .checkbox-icon {
                            .check {
                                clip-path: polygon(0 0, 100% 0, 100% 100%, 0% 100%);
                            }
                        }

                        .user-avatar.hasAvatar {
                            scale: 1.14;
                            border: 1px solid $weed-green;
                        }
                    }
                }

                .option-label {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 12px;

                    .left {
                        display: flex;
                        align-items: center;
                        gap: 12px;
                    }

                    .right {
                        display: flex;
                        align-items: center;
                        gap: 10px;
                    }

                    .hidden-checkbox {
                        display: none;
                    }
    
                    .checkbox-icon {
                        --size: 24px;
                        width: var(--size);
                        height: var(--size);
                        color: #515551;

                        .check {
                            scale: 1.8;
                            color: $weed-green;
                            transform: translate(18px, -10px) rotate(6deg);
                            transform-origin: center;
                            clip-path: polygon(0 0, 0 0, 0 100%, 0% 100%);
                            transition: .2s ease-in-out;
                        }
                    }
    
                    span {
                        color: white;
                        font-size: 20px;
                        line-height: 20px;
                    }

                    .user-avatar {
                        height: 48px;
                        margin-block: -24px;
                        margin-right: -12px;
                        aspect-ratio: 1/1;
                        border-radius: 50%;
                        scale: 0.8;
                        border: 1px solid transparent;
                        transition: 600ms cubic-bezier(.15,.75,.2,1);
                    }
                }

            }
        }
    }
}

</style>